import { z } from 'zod';

// SMS was dropped — no plans to support it (WhatsApp via Zernio covers the same need).
export const NotificationChannelSchema = z.enum(['email', 'whatsapp']);
export type NotificationChannelValue = z.infer<typeof NotificationChannelSchema>;

export const NotificationPreferenceSchema = z.object({
  channel: NotificationChannelSchema,
  enabled: z.boolean(),
});
export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;

export const NotificationSettingsSchema = z.object({
  // The verified WhatsApp number, or null until one is verified via the OTP flow below.
  phoneNumber: z.string().nullable(),
  whatsappVerified: z.boolean(),
  preferences: z.array(NotificationPreferenceSchema),
});
export type NotificationSettings = z.infer<typeof NotificationSettingsSchema>;

// Channel toggles only — the phone number is set exclusively via the OTP verify flow below,
// never through this endpoint, so a client can't silently claim an unverified number.
export const UpdateNotificationSettingsRequestSchema = z.object({
  preferences: z.array(NotificationPreferenceSchema),
});
export type UpdateNotificationSettingsRequest = z.infer<
  typeof UpdateNotificationSettingsRequestSchema
>;

// Max length matches notification_profiles.phoneNumber's varchar(20) column.
const PhoneNumberSchema = z
  .string()
  .trim()
  .max(20, 'Phone number is too long')
  .refine((v) => v.replace(/\D/g, '').length >= 10, 'Enter a valid phone number');

export const SendWhatsAppOtpRequestSchema = z.object({
  phoneNumber: PhoneNumberSchema,
});
export type SendWhatsAppOtpRequest = z.infer<typeof SendWhatsAppOtpRequestSchema>;

// Zernio Verify owns the code, its expiry and its rate limiting — there is no TTL for us to
// report. `verificationId` is required on the verify call (Zernio's check API is keyed by it).
export const SendWhatsAppOtpResponseSchema = z.object({
  status: z.literal('pending'),
  verificationId: z.string().min(1),
});
export type SendWhatsAppOtpResponse = z.infer<typeof SendWhatsAppOtpResponseSchema>;

export const VerifyWhatsAppOtpRequestSchema = z.object({
  phoneNumber: PhoneNumberSchema,
  code: z.string().trim().length(6, 'Enter the 6-digit code'),
  verificationId: z.string().min(1),
});
export type VerifyWhatsAppOtpRequest = z.infer<typeof VerifyWhatsAppOtpRequestSchema>;
